<script lang="ts">
  import { userPrefersReducedMotion } from '@pin/dom-utils';
  import { onDestroy, onMount } from 'svelte';
  import * as THREE from 'three';
  import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
  import { captureError } from '../../util/sentry';

  const prefersReducedMotion = userPrefersReducedMotion();
  const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  let loaded = false;

  let containerRef: HTMLDivElement;
  let camera: THREE.PerspectiveCamera;
  let scene: THREE.Scene;
  let renderer: THREE.WebGLRenderer;
  let cart: THREE.Group<THREE.Object3DEventMap>;
  let speed = 1;

  const isSafari =
    /iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
    (window.navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const getDimensions = () => {
    if (isMobile) {
      const { height, width } = window.screen;

      const landscape = window.innerHeight < window.innerWidth;
      // if landscape we flip height and width on iOS (god damn);
      if (landscape && isSafari) {
        return {
          height: width,
          width: height,
        };
      }
      return {
        height,
        width,
      };
    } else {
      return {
        height: window.innerHeight,
        width: window.innerWidth,
      };
    }
  };
  let { height, width } = getDimensions();

  const setSize = (force = false) => {
    const newDimensions = getDimensions();
    if (height !== newDimensions.height || newDimensions.width || force) {
      height = newDimensions.height;
      width = newDimensions.width;

      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      renderer.render(scene, camera);
    }
  };

  const loadCart = () => {
    return new Promise<void>((resolve) => {
      const loader = new OBJLoader();
      loader.load('models/cart.obj', (obj) => {
        const material = new THREE.MeshBasicMaterial({
          color: 0xffffff,
          wireframe: true,
          opacity: 0.15,
          transparent: true,
        });
        obj.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.material = material;
          }
        });

        cart = obj;

        resolve();
      });
    });
  };

  const adjustCamera = () => {
    if (window.innerWidth > 1000) {
      camera.position.set(-2.5, 4.5, 0);
      speed = 0.6;
    } else {
      camera.position.set(-3, 6, 0);
      speed = 1;
    }
    camera.lookAt(new THREE.Vector3(0, 0, 0));
  };

  const setScene = () => {
    const backgroundColor =
      import.meta.env.VITE_CLIENT_ID === 'p3-gull-artist'
        ? new THREE.Color(0x674d9b)
        : new THREE.Color(0x0063ae);
    scene.fog = new THREE.Fog(backgroundColor, -1, 25);
    scene.background = backgroundColor;

    cart.rotation.z = Math.PI;
    scene.add(cart);
  };

  const init = async () => {
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(80, width / height, 0.1, 1000);
    renderer = new THREE.WebGLRenderer({
      powerPreference: 'high-performance',
      antialias: true,
    });
    renderer.shadowMap.enabled = true;
    containerRef.appendChild(renderer.domElement);
  };

  const animate = () => {
    requestAnimationFrame(animate);

    const timer = performance.now();
    if (!prefersReducedMotion) {
      cart.rotation.y = speed * timer * 0.00002;
    }

    renderer.render(scene, camera);
  };

  const onResize = () => {
    setSize();
    adjustCamera();
  };

  onMount(async () => {
    try {
      init();
      setSize(true);
      await loadCart();
      setScene();
      adjustCamera();
      animate();
      window.addEventListener('resize', onResize);
      loaded = true;
    } catch (error) {
      captureError('webgl init failed', error);
    }
  });

  onDestroy(() => {
    window.removeEventListener('resize', onResize);
  });
</script>

<div bind:this={containerRef} class:loaded></div>

<style>
  div {
    opacity: 0;
    position: fixed;
    z-index: -1;
    bottom: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    transition: opacity 0.5s ease-in-out;
    transform: translateZ(0);
    overflow: hidden;
  }

  .loaded {
    opacity: 1;
  }
</style>
